// Tissus mous : la peau (et le tissu porté) se creuse sous le doigt puis revient avec un léger rebond ; une caresse
// (appui maintenu puis glissé sur elle) promène ce creux et la fait réagir ; les parties souples (poitrine) suivent
// les mouvements du buste avec une inertie discrète.
import * as THREE from 'three';
import type { Human } from '../human';
import type { PressUniforms } from '../render/materials/provider';

const _ray = new THREE.Raycaster();
const _v = new THREE.Vector3();
const _n = new THREE.Vector3();
const _f = new THREE.Vector3();

/** Point de contact exprimé dans l'espace de liaison (commun au corps et aux vêtements). */
export interface BindContact { point: THREE.Vector3; normal: THREE.Vector3; object: THREE.Object3D; world: THREE.Vector3 }

/**
 * Lance un rayon sur le maillage réel (corps + vêtements + cheveux, skinnés) et renvoie le point touché en espace de
 * liaison (barycentrique sur les positions de liaison du triangle).
 */
export function pickBind(human: Human, origin: THREE.Vector3, dir: THREE.Vector3, far = 10): BindContact | null {
  _ray.set(origin, dir);
  _ray.far = far;
  const targets: THREE.Object3D[] = [human.body];
  for (const [slot, m] of human.parts) if (slot === 'hair' || slot.startsWith('clothes:')) targets.push(m);
  const hits = _ray.intersectObjects(targets, false);
  // Premier impact sur une face réellement dessinée (les faces masquées sous les vêtements sont hors drawRange).
  for (const h of hits) {
    if (!h.face || !h.barycoord) continue;
    const g = (h.object as THREE.Mesh).geometry;
    const pos = g.getAttribute('position') as THREE.BufferAttribute;
    const nrm = g.getAttribute('normal') as THREE.BufferAttribute | undefined;
    const { a, b, c } = h.face, w = h.barycoord;
    const p = new THREE.Vector3()
      .addScaledVector(_v.fromBufferAttribute(pos, a), w.x)
      .addScaledVector(_v.fromBufferAttribute(pos, b), w.y)
      .addScaledVector(_v.fromBufferAttribute(pos, c), w.z);
    const n = new THREE.Vector3();
    if (nrm) {
      n.addScaledVector(_n.fromBufferAttribute(nrm, a), w.x).addScaledVector(_n.fromBufferAttribute(nrm, b), w.y)
        .addScaledVector(_n.fromBufferAttribute(nrm, c), w.z).normalize();
    }
    return { point: p, normal: n, object: h.object, world: h.point.clone() };
  }
  return null;
}

interface PressSlot {
  active: boolean;
  /** Profondeur visée (m) tant que le doigt appuie ; 0 une fois relâché. */
  goal: number;
  x: number;
  v: number;
  center: THREE.Vector3;
  radius: number;
  /** Raideur (Hz) : les zones souples rebondissent plus lentement. */
  freq: number;
  damping: number;
}

/** Pressions de doigt (4 au plus), ressort amorti par pression. */
export class SkinPress {
  /** Facteur global (réglage « Tissus souples »). */
  amount = 1;
  private slots: PressSlot[] = [0, 1, 2, 3].map(() => ({ active: false, goal: 0, x: 0, v: 0, center: new THREE.Vector3(), radius: 0.03, freq: 6, damping: 0.35 }));
  private next = 0;

  constructor(private uniforms: PressUniforms) {}

  /** Appui (renvoie l'indice de la pression, pour la suivre ou la relâcher). */
  press(center: THREE.Vector3, opts: { depth?: number; radius?: number; freq?: number; damping?: number } = {}): number {
    // Une pression libre de préférence, sinon la plus ancienne.
    let i = this.slots.findIndex((s) => !s.active && Math.abs(s.x) < 1e-4);
    if (i < 0) { i = this.next; this.next = (this.next + 1) % this.slots.length; }
    const s = this.slots[i];
    s.active = true;
    s.goal = (opts.depth ?? 0.006) * this.amount;
    s.center.copy(center);
    s.radius = opts.radius ?? 0.03;
    s.freq = opts.freq ?? 6;
    s.damping = opts.damping ?? 0.32;
    s.x = 0; s.v = 0;
    return i;
  }

  move(i: number, center: THREE.Vector3) {
    this.slots[i]?.center.copy(center);
  }

  setDepth(i: number, depth: number) {
    const s = this.slots[i];
    if (s) s.goal = depth * this.amount;
  }

  release(i: number) {
    const s = this.slots[i];
    if (s) { s.active = false; s.goal = 0; }
  }

  /** Petit tapotement : appui bref puis relâche (le rebond fait le reste). */
  tap(center: THREE.Vector3, depth = 0.006, radius = 0.03) {
    const i = this.press(center, { depth, radius, freq: 7, damping: 0.28 });
    const s = this.slots[i];
    s.v = depth * 55; // impulsion vers l'intérieur
    setTimeout(() => this.release(i), 110);
  }

  update(dt: number) {
    const P = this.uniforms.uPress.value, D = this.uniforms.uPressDepth.value;
    const n = Math.max(1, Math.ceil(dt / (1 / 240)));
    const h = Math.min(dt, 0.1) / n;
    this.slots.forEach((s, i) => {
      const w = 2 * Math.PI * s.freq, c = 2 * s.damping * w;
      for (let k = 0; k < n; k++) {
        s.v += (w * w * (s.goal - s.x) - c * s.v) * h;
        s.x += s.v * h;
      }
      if (!s.active && Math.abs(s.x) < 2e-5 && Math.abs(s.v) < 1e-3) { s.x = 0; s.v = 0; }
      P[i].set(s.center.x, s.center.y, s.center.z, s.radius);
      D[i] = s.x;
    });
  }
}

/**
 * Inertie des parties souples (os « breast.L/R » du rig MakeHuman) : un point masse par côté, relié par un ressort à
 * sa position rigide, converti en petites rotations de l'os. Amplitude volontairement réaliste et discrète ; réduite
 * quand un haut près du corps maintient la poitrine.
 */
export class SoftTissue {
  amount = 1;
  /** Maintien du vêtement (0 = libre, 1 = très maintenu). */
  support = 0.5;
  private parts: Array<{ bone: THREE.Bone; x: THREE.Vector3; v: THREE.Vector3; rest: THREE.Vector3; ready: boolean; side: number }> = [];
  private q = new THREE.Quaternion();
  private e = new THREE.Euler();
  private m = new THREE.Matrix4();
  private local = new THREE.Vector3();

  constructor(human: Human) {
    for (const [name, side] of [['breast.L', 1], ['breast.R', -1]] as const) {
      const bone = human.bones.get(name);
      if (bone) this.parts.push({ bone, x: new THREE.Vector3(), v: new THREE.Vector3(), rest: new THREE.Vector3(), ready: false, side });
    }
  }

  reset() { for (const p of this.parts) p.ready = false; }

  /** Forces de la prochaine mise à jour (une main qui appuie), accélérations en m/s². */
  private forces: Array<{ p: THREE.Vector3; a: THREE.Vector3 }> = [];
  private static weight(d2: number) { return Math.exp(-d2 / 0.012); }

  /** Impulsion (vitesse, m/s) donnée aux parties souples proches d'un point monde : un contact, une main qui glisse. */
  nudge(point: THREE.Vector3, vel: THREE.Vector3) {
    for (const p of this.parts) if (p.ready) p.v.addScaledVector(vel, SoftTissue.weight(p.x.distanceToSquared(point)));
  }

  /** Appui maintenu (accélération, m/s²) près d'un point monde, pour la prochaine mise à jour : elles restent enfoncées. */
  hold(point: THREE.Vector3, accel: THREE.Vector3) {
    if (this.forces.length < 4) this.forces.push({ p: point.clone(), a: accel.clone() });
  }

  /** À appeler après la pose (rig appliqué), avant le rendu. */
  update(dt: number) {
    const forces = this.forces;
    this.forces = [];
    if (!this.parts.length || this.amount <= 0 || dt <= 0) return;
    const amp = this.amount * (1 - 0.55 * this.support);
    const L = 0.07; // bras de levier (m) : centre de masse ~7 cm devant l'attache
    for (const p of this.parts) {
      const parent = p.bone.parent;
      if (!parent) continue;
      p.bone.updateWorldMatrix(true, false);
      // Position rigide du centre de masse : L le long de l'os (axe Y local), en monde.
      p.rest.set(0, L, 0).applyMatrix4(p.bone.matrixWorld);
      if (!p.ready || dt > 0.2) { p.x.copy(p.rest); p.v.set(0, 0, 0); p.ready = true; continue; }
      // Ressort amorti ~3,2 Hz : le centre de masse traîne derrière les mouvements du buste puis rebondit.
      const w = 2 * Math.PI * 3.2, c = 2 * 0.22 * w;
      const n = Math.max(1, Math.ceil(dt / (1 / 240)));
      const h = dt / n;
      _f.set(0, 0, 0);
      for (const f of forces) _f.addScaledVector(f.a, SoftTissue.weight(p.x.distanceToSquared(f.p)));
      for (let k = 0; k < n; k++) {
        _v.copy(p.rest).sub(p.x).multiplyScalar(w * w).addScaledVector(p.v, -c).add(_f);
        p.v.addScaledVector(_v, h);
        p.x.addScaledVector(p.v, h);
      }
      // Écart monde → repère de l'os (rotation seule), borné.
      this.m.extractRotation(p.bone.matrixWorld).invert();
      this.local.copy(p.x).sub(p.rest).applyMatrix4(this.m);
      if (this.local.length() > 0.012) this.local.setLength(0.012);
      this.local.multiplyScalar(amp);
      // Petites rotations : écart latéral (X local) → rotation autour de Z ; écart avant/arrière (Z local) → autour de X.
      this.e.set(this.local.z / L, 0, -this.local.x / L);
      this.q.setFromEuler(this.e);
      p.bone.quaternion.multiply(this.q);
    }
  }
}
